import { useState, useEffect, useMemo, useRef, useCallback } from "react"
// @ts-expect-error - @rails/actioncable doesn't have TypeScript declarations
import * as ActionCable from "@rails/actioncable"
// @ts-expect-error - currency_helper is a JavaScript file without type declarations
import { CurrencyHelper } from "~/helpers/currency_helper"
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCheckboxCell,
  DataTableGroupRow,
} from "~/react/ui/data-table"
import { DataTableBulkActionsBar } from "~/react/components/data-table-bulk-actions-bar"
import { DataTableSkeleton } from "~/react/components/data-table/data-table-skeleton"
import {
  Alert,
  AlertIcon,
  AlertContent,
  AlertDescription,
} from "~/react/ui/alert"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/react/ui/tooltip"
import { Tag, TagLabel } from "~/react/ui/tag"
import { HealthScoreIndicator } from "~/react/ui/health-score-indicator"
import { ActionsCell } from "~/react/components/data-table/cells/actions-cell"
import { useTableSelection } from "~/react/hooks/useTableSelection"
import { Skeleton } from "~/react/ui/skeleton"
import { Checkbox } from "~/react/ui/checkbox"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import {
  FiltersDropdown,
  FiltersTags,
  filterDomains,
  type DomainFilters,
  type DomainData,
  type EmailAccountOption,
} from "~/react/components/email_accounts/domains_filters"
import { SearchInput } from "~/react/ui/search-input"
import DomainSidepanel from "~/react/components/email_accounts/domain_sidepanel"
import { Sheet, SheetContent } from "~/react/ui/sheet"
import { Turbo } from "@hotwired/turbo-rails"
import { email_accounts_domains_path } from "~/lib/routes"

type DomainDataWithId = DomainData & { id: string }

type SectionedDomainData = {
  connected: DomainDataWithId[]
  managed: DomainDataWithId[]
  total: number
}

type Props = {
  total: number
  emailAccounts?: EmailAccountOption[]
}

const getFiltersFromLocation = (): DomainFilters => {
  if (typeof window === "undefined") {
    return {
      emailAccountIds: [],
      status: [],
      healthFrom: null,
      healthTo: null,
      healthIsEmpty: false,
      type: [],
      searchQuery: "",
    }
  }

  const params = new URLSearchParams(window.location.search)

  // Parse email account IDs array
  const emailAccountIdsParam = params.get("domain_email_account_ids")
  const emailAccountIds = emailAccountIdsParam
    ? emailAccountIdsParam
        .split("|")
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))
    : []

  // Parse status array
  const statusParam = params.get("domain_status")
  const status = statusParam ? statusParam.split("|") : []

  // Parse health range
  const healthFromParam = params.get("domain_health_from")
  const parsedHealthFrom = healthFromParam
    ? parseInt(healthFromParam, 10)
    : null
  const healthFrom =
    parsedHealthFrom !== null && Number.isNaN(parsedHealthFrom)
      ? null
      : parsedHealthFrom
  const healthToParam = params.get("domain_health_to")
  const parsedHealthTo = healthToParam ? parseInt(healthToParam, 10) : null
  const healthTo =
    parsedHealthTo !== null && Number.isNaN(parsedHealthTo)
      ? null
      : parsedHealthTo
  const healthIsEmpty = params.get("domain_health_empty") === "true"

  // Parse type array
  const typeParam = params.get("domain_type")
  const type = typeParam ? typeParam.split("|") : []

  // Parse search query
  const searchQuery = params.get("domain_query") || ""

  return {
    emailAccountIds,
    status,
    healthFrom,
    healthTo,
    healthIsEmpty,
    type,
    searchQuery,
  }
}

const updateFiltersInURL = (filters: DomainFilters) => {
  if (typeof window === "undefined") return

  const params = new URLSearchParams(window.location.search)

  // Update email account IDs
  if (filters.emailAccountIds.length > 0) {
    params.set("domain_email_account_ids", filters.emailAccountIds.join("|"))
  } else {
    params.delete("domain_email_account_ids")
  }

  // Update status
  if (filters.status.length > 0) {
    params.set("domain_status", filters.status.join("|"))
  } else {
    params.delete("domain_status")
  }

  // Update health range
  if (filters.healthFrom !== null) {
    params.set("domain_health_from", filters.healthFrom.toString())
  } else {
    params.delete("domain_health_from")
  }

  if (filters.healthTo !== null) {
    params.set("domain_health_to", filters.healthTo.toString())
  } else {
    params.delete("domain_health_to")
  }

  if (filters.healthIsEmpty) {
    params.set("domain_health_empty", "true")
  } else {
    params.delete("domain_health_empty")
  }

  // Update type
  if (filters.type.length > 0) {
    params.set("domain_type", filters.type.join("|"))
  } else {
    params.delete("domain_type")
  }

  // Update search query
  if (filters.searchQuery) {
    params.set("domain_query", filters.searchQuery)
  } else {
    params.delete("domain_query")
  }

  // Update URL without reload
  const queryString = params.toString()
  const newURL = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname
  window.history.replaceState(null, "", newURL)
}

function getProviderInfo(
  provider: string | null,
): { label: string; iconImage: string } | null {
  if (!provider) return null

  const providerMap: Record<string, { label: string; iconImage: string }> = {
    gmail: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.gmail"),
      iconImage: "images/connected_apps/google_icon.png",
    },
    outlook: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.outlook"),
      iconImage: "images/connected_apps/microsoft_icon.png",
    },
    smtp_imap: {
      label: i18n.t(
        "js.email_accounts_center.domains_table.providers.smtp_imap",
      ),
      iconImage: "images/connected_apps/smtp_imap_at_icon.svg",
    },
    hubspot: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.hubspot"),
      iconImage: "images/connected_apps/hubspot_icon.png",
    },
    zapier: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.zapier"),
      iconImage: "images/connected_apps/zapier_icon.png",
    },
    make: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.make"),
      iconImage: "images/connected_apps/make_icon.png",
    },
    clay: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.clay"),
      iconImage: "images/connected_apps/clay_icon.png",
    },
    zoho: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.zoho"),
      iconImage: "images/connected_apps/zoho_icon.png",
    },
    hunter: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.hunter"),
      iconImage: "images/connected_apps/hunter_icon.png",
    },
    close: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.close"),
      iconImage: "images/connected_apps/close_icon.png",
    },
    copper: {
      label: i18n.t("js.email_accounts_center.domains_table.providers.copper"),
      iconImage: "images/connected_apps/copper_icon.png",
    },
  }

  return (
    providerMap[provider] || {
      label: provider.charAt(0).toUpperCase() + provider.slice(1),
      iconImage: `images/connected_apps/${provider}_icon.png`,
    }
  )
}

function getStatusLabel(status: { active: number; inactive: number }): {
  label: string
  variant: "success" | "danger" | "warning"
} {
  const allActive = status.inactive === 0
  const allInactive = status.active === 0

  if (allActive) {
    return {
      label: i18n.t("js.email_accounts_center.domains_table.status.active"),
      variant: "success",
    }
  }
  if (allInactive) {
    return {
      label: i18n.t("js.email_accounts_center.domains_table.status.inactive"),
      variant: "danger",
    }
  }
  return {
    label: i18n.t("js.email_accounts_center.domains_table.status.mixed"),
    variant: "warning",
  }
}

const COLUMN_COUNT = 7 // checkbox + 6 data columns

export default function DomainsTable(props: Props) {
  const [rawData, setRawData] = useState<DomainData[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<DomainFilters>(() =>
    getFiltersFromLocation(),
  )
  const [activeDomain, setActiveDomain] = useState<DomainData | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const pollingRef = useRef<{
    domain: string
    initialHealth: number | null
    pollCount: number
  } | null>(null)
  const cableRef = useRef<ActionCable.Cable | null>(null)
  const subscriptionRef = useRef<ActionCable.Channel | null>(null)
  const emailAccounts = props.emailAccounts || []

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await fetch("/email-domains.json", {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const json = await response.json()
        setRawData(json)
        setError(null)
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : i18n.t(
                "js.email_accounts_center.domains_table.errors.load_failed",
              ),
        )
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Sync filters to URL whenever they change
  useEffect(() => {
    updateFiltersInURL(filters)
  }, [filters])

  // Filter domains based on filter state and search query
  const filteredData = useMemo(() => {
    return filterDomains(rawData, filters, emailAccounts)
  }, [rawData, filters, emailAccounts])

  const displayData = filteredData ?? rawData

  // Transform filtered data to sectioned format for the selection hook
  const sectionedData = useMemo<SectionedDomainData | null>(() => {
    if (!displayData) return null

    const connected: DomainDataWithId[] = displayData
      .filter((d) => d.type === "connected")
      .map((d) => ({ ...d, id: d.domain }))
    const managed: DomainDataWithId[] = displayData
      .filter((d) => d.type === "managed")
      .map((d) => ({ ...d, id: d.domain }))

    return {
      connected,
      managed,
      total: displayData.length,
    }
  }, [displayData])

  const {
    selectedIds,
    selectedCount,
    toggleSelection,
    clearSelection,
    selectAll,
    selectSection,
    allItems,
    connectedItems,
    managedItems,
    isAllSelected,
    isConnectedSectionSelected,
    isManagedSectionSelected,
    isSelected,
  } = useTableSelection(sectionedData)

  const handleRunHealthCheck = async () => {
    if (selectedIds.length === 0) return

    try {
      const response = await fetch("/email-domains/health-checks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          domain: selectedIds,
        }),
      })

      if (response.ok) {
        clearSelection()
      } else {
        const error = await response.json()
        alert(
          error.error ||
            i18n.t("js.email_accounts_center.bulk.health_check_error"),
        )
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.health_check_error"))
    }
  }

  const handleIndividualHealthCheck = async (domain: DomainData) => {
    try {
      const response = await fetch("/email-domains/health-checks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          domain: [domain.domain],
        }),
      })

      if (response.ok) {
        if (App?.displayFlash) {
          App.displayFlash(
            "success",
            i18n.t("js.email_accounts_center.actions.health_check_started"),
          )
        }
      } else {
        const error = await response.json()
        alert(
          error.error ||
            i18n.t("js.email_accounts_center.bulk.health_check_error"),
        )
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.health_check_error"))
    }
  }

  const handleCancelRenewal = useCallback(async () => {
    if (selectedIds.length === 0) return

    const confirmed = window.confirm(
      i18n.t("js.email_accounts_center.bulk.cancel_renewal_confirm", {
        count: selectedIds.length,
      }),
    )
    if (!confirmed) return

    try {
      const response = await fetch("/email-domains/bulk/subscription", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          domains: selectedIds,
        }),
      })

      if (response.status === 202) {
        if (App?.displayFlash) {
          App.displayFlash(
            "success",
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_queued", {
              count: selectedIds.length,
            }),
          )
        }
        clearSelection()
      } else if (response.ok) {
        if (App?.displayFlash) {
          App.displayFlash(
            "success",
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_success", {
              count: selectedIds.length,
            }),
          )
        }
        clearSelection()
      } else {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }))
        alert(
          error.error ||
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"),
        )
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    }
  }, [selectedIds, clearSelection])

  const handleIndividualCancelRenewal = async (domain: DomainData) => {
    if (domain.type !== "managed") return

    const confirmed = window.confirm(
      i18n.t("js.email_accounts_center.bulk.cancel_renewal_confirm", {
        count: 1,
      }),
    )
    if (!confirmed) return

    try {
      const response = await fetch(
        `/email-domains/${encodeURIComponent(domain.domain)}/subscription`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            Accept: "text/vnd.turbo-stream.html, application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
          credentials: "same-origin",
        },
      )

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType?.includes("text/vnd.turbo-stream.html")) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        }

        if (App?.displayFlash) {
          App.displayFlash(
            "success",
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_success", {
              count: 1,
            }),
          )
        }
      } else {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }))
        alert(
          error.error ||
            i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"),
        )
      }
    } catch (err) {
      alert(i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    }
  }

  // Compute bulk actions based on selected domains
  const bulkActions = useMemo(() => {
    const actions = [
      {
        label: i18n.t("js.email_accounts_center.bulk.run_health_check"),
        icon: "fa-shield-check",
        onClick: () => {
          void handleRunHealthCheck()
        },
        disabled: selectedCount === 0,
      },
    ]

    // Only include "Cancel renewal" if all selected domains are managed
    if (selectedCount > 0) {
      const selectedDomains = allItems.filter((domain) =>
        selectedIds.includes(domain.domain),
      )
      const allAreManaged = selectedDomains.every(
        (domain) => domain.type === "managed",
      )

      if (allAreManaged) {
        actions.push({
          label: i18n.t("js.email_accounts_center.actions.cancel_renewal"),
          icon: "fa-times-circle",
          onClick: () => {
            void handleCancelRenewal()
          },
          disabled: selectedCount === 0,
        })
      }
    }

    return actions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, selectedCount, allItems])

  const renderDomainRow = (domain: DomainData, section?: string) => {
    const statusInfo = getStatusLabel(domain.status)
    return (
      <DataTableRow key={domain.domain} id={domain.domain}>
        <DataTableCheckboxCell
          aria-label={i18n.t("js.data_table.select_record", {
            record: domain.domain,
          })}
          recordId={domain.domain}
          section={section}
          checked={isSelected(domain.domain)}
          onChange={() => toggleSelection(domain.domain)}
        />
        <DataTableCell sticky>
          <Button
            variant="ghost"
            size="xs"
            className="lead-tag lead-tag--people"
            type="button"
            onClick={() => setActiveDomain(domain)}
          >
            <span className="lead-tag__name">{domain.domain}</span>
          </Button>
        </DataTableCell>
        <DataTableCell>
          <Tag variant={statusInfo.variant}>
            <TagLabel>{statusInfo.label}</TagLabel>
          </Tag>
        </DataTableCell>
        <DataTableCell>
          {domain.email_accounts_count > 0 ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    className="h-data-table-cell__link"
                    href={`/campaigns/email-accounts-center?query=${encodeURIComponent(domain.domain)}`}
                  >
                    {i18n.t(
                      "js.email_accounts_center.domains_table.email_account",
                      {
                        count: domain.email_accounts_count,
                      },
                    )}
                  </a>
                </TooltipTrigger>
                {domain.email_accounts && domain.email_accounts.length > 0 && (
                  <TooltipContent>
                    <div className="d-flex flex-column gap-1">
                      {domain.email_accounts.map((email) => (
                        <span key={email}>{email}</span>
                      ))}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="text-xs text-body-secondary">
              {i18n.t(
                "js.email_accounts_center.domains_table.email_account.other",
                {
                  count: 0,
                },
              )}
            </span>
          )}
        </DataTableCell>
        <DataTableCell>
          <HealthScoreIndicator score={domain.health} />
        </DataTableCell>
        <DataTableCell>
          {(() => {
            const providerInfo = getProviderInfo(domain.provider)
            if (providerInfo) {
              return (
                <>
                  <img
                    src={`${import.meta.env.BASE_URL || ""}${providerInfo.iconImage}`}
                    alt=""
                    className="p-05"
                    width={16}
                    height={16}
                  />
                  {providerInfo.label}
                </>
              )
            }
            // Fallback to type if no provider
            return (
              <>
                <img
                  src={`${import.meta.env.BASE_URL || ""}images/connected_apps/smtp_imap_at_icon.svg`}
                  alt=""
                  className="p-05"
                  width={16}
                  height={16}
                />
                {i18n.t(
                  "js.email_accounts_center.domains_table.providers.smtp_imap",
                )}
              </>
            )
          })()}
        </DataTableCell>
        <DataTableCell>
          <ActionsCell
            primaryAction={{
              label: i18n.t("js.email_accounts_center.actions.view"),
              onClick: () => {
                setActiveDomain(domain)
              },
            }}
            moreActions={
              domain.type === "managed"
                ? [
                    {
                      label: i18n.t(
                        "js.email_accounts_center.actions.cancel_renewal",
                      ),
                      onClick: () => handleIndividualCancelRenewal(domain),
                      variant: "danger" as const,
                    },
                    {
                      label: i18n.t(
                        "js.email_accounts_center.actions.run_health_check",
                      ),
                      onClick: () => handleIndividualHealthCheck(domain),
                    },
                  ]
                : []
            }
            moreActionsAriaLabel={i18n.t(
              "js.email_accounts_center.actions.more_actions_aria",
            )}
          />
        </DataTableCell>
      </DataTableRow>
    )
  }

  // Poll the domain until health changes or max attempts reached (fallback when no push arrives)
  useEffect(() => {
    if (!isPolling || !pollingRef.current) return

    // Allow up to ~60s of polling (20 * 3s) to match email accounts behavior
    const maxPolls = 20
    const pollInterval = 3000

    const pollForHealthUpdate = async () => {
      if (!pollingRef.current) return
      const { domain, initialHealth } = pollingRef.current

      try {
        const response = await fetch(
          `/email-domains/${encodeURIComponent(domain)}`,
          {
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            credentials: "same-origin",
          },
        )

        if (response.ok) {
          const updatedDomain: DomainData = await response.json()

          setRawData((prev) =>
            prev
              ? prev.map((d) =>
                  d.domain === updatedDomain.domain ? updatedDomain : d,
                )
              : prev,
          )

          // Update the sidepanel if it is showing this domain
          setActiveDomain((prev) =>
            prev && prev.domain === updatedDomain.domain ? updatedDomain : prev,
          )

          const hasHealthChanged =
            updatedDomain.health !== undefined &&
            updatedDomain.health !== null &&
            updatedDomain.health !== initialHealth

          if (hasHealthChanged) {
            setIsPolling(false)
            pollingRef.current = null
            return
          }
        }
      } catch (err) {
        console.error("Failed to poll domain health:", err)
      }

      if (!pollingRef.current) return
      pollingRef.current.pollCount += 1
      if (pollingRef.current.pollCount >= maxPolls) {
        setIsPolling(false)
        pollingRef.current = null
      }
    }

    const intervalId = setInterval(pollForHealthUpdate, pollInterval)
    const timeoutId = setTimeout(pollForHealthUpdate, 1000)

    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
  }, [isPolling])

  // Subscribe to backend push notifications for completed health checks
  useEffect(() => {
    if (subscriptionRef.current) return

    const consumer = ActionCable.createConsumer()
    cableRef.current = consumer

    const subscription = consumer.subscriptions.create(
      { channel: "HealthChecksChannel" },
      {
        received: (data: {
          domain: string
          health: number | null
          status: string | null
        }) => {
          const { domain, health } = data

          setRawData((prev) =>
            prev
              ? prev.map((d) => (d.domain === domain ? { ...d, health } : d))
              : prev,
          )

          setActiveDomain((prev) =>
            prev && prev.domain === domain ? { ...prev, health } : prev,
          )

          if (pollingRef.current?.domain === domain) {
            setIsPolling(false)
            pollingRef.current = null
          }
        },
      },
    )

    subscriptionRef.current = subscription

    return () => {
      subscription.unsubscribe()
      consumer.disconnect()
      subscriptionRef.current = null
      cableRef.current = null
    }
  }, [])

  const skeletonColumns = [
    { label: i18n.t("js.email_accounts_center.domains_table.columns.domain") },
    { label: i18n.t("js.email_accounts_center.domains_table.columns.status") },
    {
      label: i18n.t(
        "js.email_accounts_center.domains_table.columns.email_accounts",
      ),
    },
    { label: i18n.t("js.email_accounts_center.domains_table.columns.health") },
    { label: i18n.t("js.email_accounts_center.domains_table.columns.type") },
  ]

  if (loading) {
    return (
      <div>
        <div className="main-heading">
          <h2 className="main-heading__count">
            <Skeleton
              size="freeform"
              style={{ width: "10.4rem", height: "1.6rem" }}
            />
          </h2>
        </div>

        <DataTableSkeleton
          columns={skeletonColumns}
          checkboxAriaLabel={i18n.t(
            "js.email_accounts_center.domains_table.bulk.select_all",
          )}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className="main-heading">
          <h2 className="main-heading__count">
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t(
                  "js.email_accounts_center.domains_table.count_html",
                  {
                    pretty_count: CurrencyHelper.numberWithDelimiter(
                      props.total,
                    ),
                    count: props.total,
                  },
                ),
              }}
            />
          </h2>

          <hr className="main-heading__separator" />

          <div className="main-heading__filtering">
            <SearchInput
              value={filters.searchQuery || ""}
              onChange={(value: string) =>
                setFilters((prev) => ({ ...prev, searchQuery: value }))
              }
              placeholder={i18n.t(
                "js.email_accounts_center.domains_table.search_placeholder",
              )}
              ariaLabel={i18n.t(
                "js.email_accounts_center.domains_table.search_aria",
              )}
              clearAriaLabel={i18n.t("js.email_accounts_center.clear_search")}
            />
            <FiltersDropdown
              filters={filters}
              setFilters={setFilters}
              emailAccounts={emailAccounts}
              domains={[]}
            />
          </div>
        </div>

        <Alert variant="destructive" className="mb-3">
          <AlertIcon>
            <span className="h-alert__icon far fa-exclamation-circle"></span>
          </AlertIcon>
          <AlertContent>
            <AlertDescription>
              {i18n.t("js.email_accounts_center.domains_table.errors.alert")}
            </AlertDescription>
          </AlertContent>
        </Alert>

        <DataTableSkeleton
          columns={skeletonColumns}
          checkboxAriaLabel={i18n.t(
            "js.email_accounts_center.domains_table.bulk.select_all",
          )}
        />
      </div>
    )
  }

  if (!sectionedData) {
    return (
      <div>
        <div className="main-heading">
          <h2 className="main-heading__count">
            <span
              dangerouslySetInnerHTML={{
                __html: i18n.t(
                  "js.email_accounts_center.domains_table.count_html",
                  {
                    pretty_count: CurrencyHelper.numberWithDelimiter(0),
                    count: 0,
                  },
                ),
              }}
            />
          </h2>
        </div>
      </div>
    )
  }

  const totalCount = sectionedData.total || props.total
  const connectedCount = connectedItems.length
  const managedCount = managedItems.length
  const connectedActiveCount = (connectedItems as DomainDataWithId[]).filter(
    (d) => d.status.inactive === 0,
  ).length
  const managedActiveCount = (managedItems as DomainDataWithId[]).filter(
    (d) => d.status.inactive === 0,
  ).length

  return (
    <div>
      <div className="main-heading">
        <h2 className="main-heading__count">
          <span
            dangerouslySetInnerHTML={{
              __html: i18n.t("js.email_accounts_center.count_html", {
                pretty_count: CurrencyHelper.numberWithDelimiter(totalCount),
                count: totalCount,
              }),
            }}
          />
        </h2>

        <hr className="main-heading__separator" />

        <div className="main-heading__filtering">
          <SearchInput
            value={filters.searchQuery || ""}
            onChange={(value: string) =>
              setFilters((prev) => ({ ...prev, searchQuery: value }))
            }
            placeholder={i18n.t(
              "js.email_accounts_center.domains_table.search_placeholder",
            )}
            ariaLabel={i18n.t(
              "js.email_accounts_center.domains_table.search_aria",
            )}
            clearAriaLabel={i18n.t("js.email_accounts_center.clear_search")}
          />
          <FiltersDropdown
            filters={filters}
            setFilters={setFilters}
            emailAccounts={emailAccounts}
            domains={displayData || []}
          />
        </div>
      </div>

      <FiltersTags
        filters={filters}
        setFilters={setFilters}
        emailAccounts={emailAccounts}
      />

      <DataTable
        bulkActionsBar={
          <DataTableBulkActionsBar
            totalCount={totalCount}
            pageCount={allItems.length}
            selectedCount={selectedCount}
            onClearSelection={clearSelection}
            onSelectAll={() => selectAll(allItems)}
            onSelectPage={() => selectAll(allItems)}
            actions={bulkActions}
          />
        }
      >
        <DataTableHeader>
          <DataTableHeaderCell className="h-data-table__checkbox">
            <div className="h-data-table-cell h-data-table-cell--checkbox">
              <Checkbox
                aria-label={i18n.t(
                  "js.email_accounts_center.domains_table.bulk.select_all",
                )}
                checked={isAllSelected}
                onChange={() => selectAll(isAllSelected ? [] : allItems)}
              />
            </div>
          </DataTableHeaderCell>
          <DataTableHeaderCell sticky>
            {i18n.t("js.email_accounts_center.domains_table.columns.domain")}
          </DataTableHeaderCell>
          <DataTableHeaderCell>
            {i18n.t("js.email_accounts_center.domains_table.columns.status")}
          </DataTableHeaderCell>
          <DataTableHeaderCell>
            {i18n.t(
              "js.email_accounts_center.domains_table.columns.email_accounts",
            )}
          </DataTableHeaderCell>
          <DataTableHeaderCell>
            {i18n.t("js.email_accounts_center.domains_table.columns.health")}
          </DataTableHeaderCell>
          <DataTableHeaderCell>
            {i18n.t("js.email_accounts_center.domains_table.columns.type")}
          </DataTableHeaderCell>
          <DataTableHeaderCell></DataTableHeaderCell>
        </DataTableHeader>

        <DataTableBody>
          {/* Connected domains group */}
          {connectedCount > 0 && (
            <>
              <DataTableGroupRow
                colSpan={COLUMN_COUNT}
                sectionId="connected"
                checkboxCell={
                  <Checkbox
                    aria-label={i18n.t(
                      "js.email_accounts_center.domains_table.bulk.select_connected_group",
                    )}
                    checked={isConnectedSectionSelected}
                    onChange={() => selectSection("connected")}
                  />
                }
              >
                <strong className="fw-medium text-body-secondary mr-1">
                  {i18n.t(
                    "js.email_accounts_center.domains_table.groups.connected_domains",
                  )}
                </strong>
                <span className="text-xs text-hint">
                  · {connectedActiveCount}/{connectedCount}
                </span>
              </DataTableGroupRow>

              {(connectedItems as DomainDataWithId[]).map((domain) =>
                renderDomainRow(domain, "connected"),
              )}
            </>
          )}

          {/* Managed domains group */}
          {managedCount > 0 && (
            <>
              <DataTableGroupRow
                colSpan={COLUMN_COUNT}
                sectionId="managed"
                checkboxCell={
                  <Checkbox
                    aria-label={i18n.t(
                      "js.email_accounts_center.domains_table.bulk.select_managed_group",
                    )}
                    checked={isManagedSectionSelected}
                    onChange={() => selectSection("managed")}
                  />
                }
              >
                <strong className="fw-medium text-body-secondary mr-1">
                  {i18n.t(
                    "js.email_accounts_center.domains_table.groups.managed_domains",
                  )}
                </strong>
                <span className="text-xs text-hint">
                  · {managedActiveCount}/{managedCount}
                </span>
                <Button size="xs" className="ml-auto" type="button" asChild>
                  <a href={email_accounts_domains_path()} data-turbo="false">
                    <Icon icon="plus" className="mr-1" aria-hidden="true" />
                    {i18n.t("js.email_accounts_center.groups.create_domains")}
                  </a>
                </Button>
              </DataTableGroupRow>

              {(managedItems as DomainDataWithId[]).map((domain) =>
                renderDomainRow(domain, "managed"),
              )}
            </>
          )}
        </DataTableBody>
      </DataTable>
      <Sheet
        open={!!activeDomain}
        modal={false}
        onOpenChange={(open) => !open && setActiveDomain(null)}
      >
        <SheetContent side="right" size="md" className="p-0">
          {activeDomain && (
            <DomainSidepanel
              domain={activeDomain}
              onHealthCheckTriggered={() => {
                pollingRef.current = {
                  domain: activeDomain.domain,
                  initialHealth: activeDomain.health ?? null,
                  pollCount: 0,
                }
                setIsPolling(true)
              }}
              onDomainUpdated={(updatedDomain) => {
                // Update the domain in the list
                if (rawData) {
                  const updatedData = rawData.map((d) =>
                    d.domain === updatedDomain.domain ? updatedDomain : d,
                  )
                  setRawData(updatedData)
                  setActiveDomain(updatedDomain)
                }
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
